import { cn } from '@/lib/utils'
import type { EvaluationTemplate } from '@/lib/types'

export interface TrendPoint {
  /** ISO date of the run, for the axis. */
  on: string
  /** The run's average for this area, or null if the run never scored it. */
  value: number | null
  /** Run name, for the point's tooltip. */
  runName: string
}

export interface TrendSeries {
  label: string
  points: TrendPoint[]
}

/**
 * How every area of an evaluation has moved across every run of it.
 *
 * ## Why small multiples rather than one chart with ten lines
 *
 * The obvious build is a single line chart with one line per knowledge area. It
 * is also unreadable: SWEBOK has **ten** areas and the Process assessment seven,
 * and a categorical palette runs out at seven or eight before colours start
 * colliding for a colourblind reader. Ten lines on one axis is spaghetti — the
 * reader can trace maybe two of them, and the answer to "which areas are
 * improving" is exactly what gets lost.
 *
 * So each area gets its own small chart on a shared scale. One series per facet
 * means no categorical palette at all: every line is the same single-series
 * green, identity comes from the heading above each facet, and there is no
 * legend to read. Comparing facets is a saccade instead of a colour lookup.
 *
 * ## What it deliberately doesn't do
 *
 * - **No shared x-scale by calendar distance.** Runs are placed evenly, not by
 *   how many weeks apart they were, because a team that evaluated twice in March
 *   and once in November would otherwise squash two thirds of its own history
 *   into a corner. The axis is labelled with real dates so the spacing is never
 *   mistaken for time.
 * - **No y-axis starting at zero.** The scale is the template's own (1–5), which
 *   is the full range a score can take. Zero is not on the scale.
 * - **No number on every point.** Only the latest value is labelled; the rest are
 *   in each point's tooltip and in the aria summary, so the facet stays readable
 *   at this size.
 *
 * Fixed-viewBox SVG rather than a chart library, for the same reason as
 * `radar-chart.tsx`: the marks wear the theme's own tokens, and the monthly
 * report prints to PDF where nothing can wait for a container measurement.
 */
export function CategoryTrend({
  series,
  template,
  className,
}: {
  series: TrendSeries[]
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max'>
  className?: string
}) {
  if (series.length === 0) return null

  return (
    <div className={cn('grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {series.map((s) => (
        <Facet key={s.label} series={s} template={template} />
      ))}
    </div>
  )
}

// One area. Sized so three sit across a card on a laptop without the line
// becoming a flat thread.
const W = 240
const H = 92
// The scale numbers live on the LEFT and the latest value on the RIGHT, because
// they collide otherwise: the value is printed beside the final point, and a
// score at the top of the scale puts it exactly where a right-hand "5" would be.
// A team that reaches 5.0 in an area is the case this feature exists to show, so
// it must not be the case that breaks the label.
const PAD = { left: 16, right: 30, top: 10, bottom: 18 }

function Facet({
  series,
  template,
}: {
  series: TrendSeries
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max'>
}) {
  const min = template.scale_min
  const max = template.scale_max
  const pts = series.points
  const n = pts.length

  const plotW = W - PAD.left - PAD.right
  const plotH = H - PAD.top - PAD.bottom

  // A single run has no "distance" to spread across, so it sits at the left
  // rather than dividing by zero.
  const x = (i: number) => (n <= 1 ? PAD.left : PAD.left + (i * plotW) / (n - 1))
  const y = (v: number) => PAD.top + (1 - (v - min) / (max - min)) * plotH

  const scored = pts.filter((p): p is TrendPoint & { value: number } => p.value != null)
  const first = scored[0] ?? null
  const last = scored[scored.length - 1] ?? null
  const delta = first && last && scored.length > 1 ? last.value - first.value : null

  // A run that skipped this area breaks the line rather than being interpolated
  // across — joining 2.0 to 4.0 through a gap draws a climb that was never
  // measured. Consecutive scored runs form each segment.
  const segments: Array<Array<{ i: number; value: number }>> = []
  let run: Array<{ i: number; value: number }> = []
  pts.forEach((p, i) => {
    if (p.value == null) {
      if (run.length > 0) segments.push(run)
      run = []
    } else {
      run.push({ i, value: p.value })
    }
  })
  if (run.length > 0) segments.push(run)

  const summary = pts
    .map((p) => `${fmtDate(p.on)}: ${p.value == null ? 'not scored' : p.value.toFixed(1)}`)
    .join('; ')

  const midline = min + (max - min) / 2

  return (
    <figure className="min-w-0">
      <figcaption className="mb-0.5 flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-medium" title={series.label}>
          {series.label}
        </span>
        {/* Direction is carried by the arrow glyph, not by colour — a red/green
            delta would be the one thing on this page a colourblind reader
            couldn't read, and it would also be the most important thing. */}
        {delta != null && Math.abs(delta) >= 0.05 && (
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
            {delta > 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}
          </span>
        )}
      </figcaption>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`${series.label}, scale ${min} to ${max}. ${summary}`}
      >
        {/* Solid hairlines, one shade off the surface: floor, midpoint, ceiling.
            Every ring on the radar carries its number; here only the outer two
            do, because at this size five labelled rings is more ink than line. */}
        <g stroke="hsl(var(--chart-grid))" strokeWidth={1}>
          <line x1={PAD.left} y1={y(max)} x2={W - PAD.right} y2={y(max)} />
          <line x1={PAD.left} y1={y(midline)} x2={W - PAD.right} y2={y(midline)} />
          <line x1={PAD.left} y1={y(min)} x2={W - PAD.right} y2={y(min)} />
        </g>
        <g
          fill="hsl(var(--muted-foreground))"
          fontSize={8}
          textAnchor="end"
          className="tabular-nums"
        >
          <text x={PAD.left - 4} y={y(max) + 3}>
            {max}
          </text>
          <text x={PAD.left - 4} y={y(min) + 3}>
            {min}
          </text>
        </g>

        {segments.map((seg, si) => (
          <polyline
            key={si}
            fill="none"
            stroke="var(--chart-line)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            points={seg.map((p) => `${x(p.i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')}
          />
        ))}

        {/* A lone scored run draws no polyline, so the dot below is the whole
            mark — which is correct: one measurement is a position, not a trend. */}
        {pts.map((p, i) =>
          p.value == null ? null : (
            <circle
              key={i}
              cx={x(i)}
              cy={y(p.value)}
              r={i === lastScoredIndex(pts) ? 4 : 3}
              fill={i === lastScoredIndex(pts) ? 'var(--chart-line)' : 'hsl(var(--card))'}
              stroke="var(--chart-line)"
              strokeWidth={2}
            >
              <title>{`${p.runName} · ${fmtDate(p.on)} · ${p.value.toFixed(1)}`}</title>
            </circle>
          ),
        )}

        {/* Only the latest value is printed. */}
        {last && (
          <text
            x={x(lastScoredIndex(pts)) + 8}
            y={y(last.value) + 3}
            fontSize={10}
            className="tabular-nums"
            fill="hsl(var(--foreground))"
            fontWeight={600}
          >
            {last.value.toFixed(1)}
          </text>
        )}

        {/* First and last run only. Labelling every run collides as soon as a
            team has four of them. */}
        <g fill="hsl(var(--muted-foreground))" fontSize={8}>
          <text x={PAD.left} y={H - 4}>
            {fmtDate(pts[0]?.on)}
          </text>
          {n > 1 && (
            <text x={W - PAD.right} y={H - 4} textAnchor="end">
              {fmtDate(pts[n - 1]?.on)}
            </text>
          )}
        </g>
      </svg>
    </figure>
  )
}

/** Index of the most recent run that actually scored this area. */
function lastScoredIndex(pts: TrendPoint[]): number {
  for (let i = pts.length - 1; i >= 0; i--) if (pts[i].value != null) return i
  return -1
}

/** "May ’26" — short enough for an 8px axis label, unambiguous across years. */
function fmtDate(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.toLocaleDateString(undefined, { month: 'short' })} ’${String(d.getFullYear()).slice(2)}`
}
